import { VENDOR_TYPE_SPECIALITY_MAP } from '../constants/providerSearchConstants.js';
import type { InitialValues } from './validationConfig.js';
import type { FieldConfig } from './providerSearchConfig.js';
import { ALPHANUMERIC_REGEX, NUMERIC_ONLY_REGEX, COUNTY_INPUT_REGEX, ZIP_INPUT_REGEX } from '../constants/validationConstants.js';
import { apiGet } from '../services/apiClient';
import { loadConfig } from '../envconfig';

// Reference Code API Response Type
interface ReferenceCodeItem {
  codeValue: string;
  codeDescShort: string;
  codeDescLong: string;
  sortOrder: number;
  categoryDescShort: string;
  categoryDescLong: string;
  updated: string;
  lastDbRetrieved: string;
}

// Dropdown option format
interface DropdownOption {
  id: string;
  value: string;
}

// Cache for reference codes to avoid redundant API calls
let referenceCodesCache: ReferenceCodeItem[] | null = null;
let cacheTimestamp: number | null = null;
let pendingFetch: Promise<ReferenceCodeItem[]> | null = null; // Track pending API call
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch reference codes from API with caching and deduplication
 * Multiple simultaneous calls will share the same API request
 */
async function fetchReferenceCodes(): Promise<ReferenceCodeItem[]> {
  const now = Date.now();

  // Return cached data if it's still valid
  if (referenceCodesCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    return referenceCodesCache;
  }

  // If a fetch is already in progress, return that promise
  if (pendingFetch) {
    return pendingFetch;
  }

  // Start a new fetch
  pendingFetch = (async () => {
    try {
      const { API_URL } = await loadConfig();
      const data: ReferenceCodeItem[] = await apiGet(`${API_URL}/ReferenceCode/reference-codes`);
      referenceCodesCache = data;
      cacheTimestamp = now;
      return data;
    } catch (error) {
      console.error('❌ [Reference Codes] Error fetching reference codes:', error);
      if (error instanceof Error && error.message.includes('401')) {
        console.error('🔒 [Reference Codes] Authorization error - token may be missing, invalid, or expired');
      }
      // Return empty array on error
      return [];
    } finally {
      // Clear pending fetch after completion
      pendingFetch = null;
    }
  })();

  return pendingFetch;
}

/**
 * Get dropdown options by category
 */
async function getOptionsByCategory(category: string): Promise<DropdownOption[]> {
  const referenceCodes = await fetchReferenceCodes();

  const filtered = referenceCodes
    .filter(item => item.categoryDescShort === category)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(item => ({
      id: item.codeValue,
      value: item.codeDescLong
    }));
  return filtered;
}

/**
 * Get Customer Network Codes (Business Approval Codes)
 */
export async function getCustomerNetworkCodes(): Promise<DropdownOption[]> {
  return await getOptionsByCategory('BUSINESSAPPROVALCODE');
}

/**
 * Get Auto Network Selection Options
 */
export async function getAutoNetworkSelectionOptions(): Promise<DropdownOption[]> {
  return await getOptionsByCategory('AUTONETWORKSELECTION');
}

/**
 * Get Claim Center Options
 */
export async function getClaimCenterOptions(): Promise<DropdownOption[]> {
  return await getOptionsByCategory('CLAIMOFFICECENTER');
}

/**
 * Get Appraisal Software Options
 */
export async function getAppraisalSoftwareOptions(): Promise<DropdownOption[]> {
  return await getOptionsByCategory('AUTOAPPRAISALSOFTWARE');
}

/**
 * Get Provider Status Options
 */
export async function getProviderStatusOptions(): Promise<DropdownOption[]> {
  return await getOptionsByCategory('PROVIDERSTATUS');
}

/**
 * Get Vendor Type Code Options
 */
export async function getVendorTypeCodeOptions(): Promise<DropdownOption[]> {
  return await getOptionsByCategory('TRAVREFERRALVENDORTYPE');
}



/**
 * Temporary hardcoded US states - to be removed once backend adds states to reference codes API
 */
const TEMP_US_STATES = [
  { id: 'AL', value: 'Alabama' }, { id: 'AK', value: 'Alaska' }, { id: 'AZ', value: 'Arizona' },
  { id: 'AR', value: 'Arkansas' }, { id: 'CA', value: 'California' }, { id: 'CO', value: 'Colorado' },
  { id: 'CT', value: 'Connecticut' }, { id: 'DE', value: 'Delaware' }, { id: 'FL', value: 'Florida' },
  { id: 'GA', value: 'Georgia' }, { id: 'HI', value: 'Hawaii' }, { id: 'ID', value: 'Idaho' },
  { id: 'IL', value: 'Illinois' }, { id: 'IN', value: 'Indiana' }, { id: 'IA', value: 'Iowa' },
  { id: 'KS', value: 'Kansas' }, { id: 'KY', value: 'Kentucky' }, { id: 'LA', value: 'Louisiana' },
  { id: 'ME', value: 'Maine' }, { id: 'MD', value: 'Maryland' }, { id: 'MA', value: 'Massachusetts' },
  { id: 'MI', value: 'Michigan' }, { id: 'MN', value: 'Minnesota' }, { id: 'MS', value: 'Mississippi' },
  { id: 'MO', value: 'Missouri' }, { id: 'MT', value: 'Montana' }, { id: 'NE', value: 'Nebraska' },
  { id: 'NV', value: 'Nevada' }, { id: 'NH', value: 'New Hampshire' }, { id: 'NJ', value: 'New Jersey' },
  { id: 'NM', value: 'New Mexico' }, { id: 'NY', value: 'New York' }, { id: 'NC', value: 'North Carolina' },
  { id: 'ND', value: 'North Dakota' }, { id: 'OH', value: 'Ohio' }, { id: 'OK', value: 'Oklahoma' },
  { id: 'OR', value: 'Oregon' }, { id: 'PA', value: 'Pennsylvania' }, { id: 'RI', value: 'Rhode Island' },
  { id: 'SC', value: 'South Carolina' }, { id: 'SD', value: 'South Dakota' }, { id: 'TN', value: 'Tennessee' },
  { id: 'TX', value: 'Texas' }, { id: 'UT', value: 'Utah' }, { id: 'VT', value: 'Vermont' },
  { id: 'VA', value: 'Virginia' }, { id: 'WA', value: 'Washington' }, { id: 'WV', value: 'West Virginia' },
  { id: 'WI', value: 'Wisconsin' }, { id: 'WY', value: 'Wyoming' }
];

/**
 * Get State Options
 * TODO: Remove hardcoded states once backend adds states to reference codes API
 */
export async function getStateOptions(): Promise<DropdownOption[]> {
  const referenceCodes = await fetchReferenceCodes();

  // Try to get states from API first
  const stateOptions = referenceCodes
    .filter(item =>
      item.categoryDescShort === 'STATE' ||
      item.categoryDescShort === 'STATES' ||
      item.categoryDescShort === 'STATE_NUMERIC' ||
      item.categoryDescShort === 'state_numeric'
    )
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(item => ({
      id: item.codeDescShort,
      value: item.codeDescLong
    }));

  return stateOptions.filter(opt => opt.id && opt.value);
}


/**
 * Get Contract Type Options
 * Fetches contract type options and handles PR/PF mapping for Procurement Form
 */
export async function getContractTypeOptions(): Promise<DropdownOption[]> {
  const options = await getOptionsByCategory('CONTRACTTYPE');

  // Map the options and handle both PF and PR codes for Procurement Form
  return options.map(option => {
    // Check if this is Procurement Form (PF code)
    if (option.id === 'PF' || option.id === 'PR') {
      return {
        id: 'PR', // Store both codes separated by pipe
        value: option.value
      };
    }
    return option;
  }).filter((option, index, self) =>
    // Remove duplicates by checking if this is the first occurrence of this value
    index === self.findIndex(t => t.value === option.value)
  );
}


/**
 * Get Property Vendor Type Options
 * Fetches vendor types for property search
 */
export async function getPropertyVendorTypeOptions(): Promise<DropdownOption[]> {
  return await getOptionsByCategory('PROPERTYVENDORTYPE');
}

/**
 * Get Property Vendor Specialty Options
 * Fetches all specialty options for property vendors
 */
/**
 * Get Property Vendor Specialty Options
 * For 'local' property vendorType, returns only the four specified options.
 * For all other cases, returns all options.
 * @param mode - 'local' (default) returns only 4 options, 'all' returns all options
 */
/**
 * Get Property Vendor Specialty Options
 * @param mode - 'local' returns only 4 options for dropdown, 'all' returns all options
 */
export async function getPropertyVendorSpecialtyOptions(mode: 'local' | 'all' = 'local'): Promise<DropdownOption[]> {
  const allOptions = await getOptionsByCategory('PROPERTYVENDORSPECIALTY');
  if (mode === 'local') {
    // Only keep these options for local dropdown
    return allOptions.filter(opt =>
      opt.value === 'Dry Cleaning Svcs' ||
      opt.value === 'BOARD UP Services' ||
      opt.value === 'Emergency Svcs/General'
    );
  }
  // For 'all' mode, return all options
  return allOptions;
}

/**
 * Always get all specialty options for mapping codes to descriptions (for grid display)
 */
export async function getAllPropertyVendorSpecialtyOptions(): Promise<DropdownOption[]> {
  return await getPropertyVendorSpecialtyOptions('all');
}
/**
 * Build Property Vendor Specialty Map
 * Creates a dynamic mapping of vendor type codes to their respective specialty options
 * Uses the VENDOR_TYPE_SPECIALITY_MAP from constants as the structure, populated with API data
 */
export async function buildPropertyVendorSpecialtyMap(): Promise<Record<string, DropdownOption[]>> {
  // Fetch all specialties from API
  const specialties = await getPropertyVendorSpecialtyOptions();

  // Create a lookup map for specialties by code
  const specialtyLookup: Record<string, DropdownOption> = {};
  specialties.forEach(specialty => {
    if (specialty.id) {
      specialtyLookup[specialty.id] = specialty;
    }
  });

  // Build the map using the structure from VENDOR_TYPE_SPECIALITY_MAP but with API data
  const specialtyMap: Record<string, DropdownOption[]> = {};

  Object.keys(VENDOR_TYPE_SPECIALITY_MAP).forEach(vendorTypeCode => {
    specialtyMap[vendorTypeCode] = [{ id: '', value: '' }];

    // Get the specialty IDs for this vendor type from the hardcoded map
    const hardcodedSpecialties = VENDOR_TYPE_SPECIALITY_MAP[vendorTypeCode];

    if (hardcodedSpecialties) {
      hardcodedSpecialties.forEach(specialty => {
        if (specialty.id && specialtyLookup[specialty.id]) {
          // Use API data if available
          const apiSpecialty = specialtyLookup[specialty.id];
          if (apiSpecialty && specialtyMap[vendorTypeCode]) {
            specialtyMap[vendorTypeCode].push(apiSpecialty);
          }
        } else if (specialty.id && specialtyMap[vendorTypeCode]) {
          // Fallback to hardcoded data if not in API yet
          specialtyMap[vendorTypeCode].push(specialty);
        }
      });
    }
  });

  return specialtyMap;
}

/**
 * Get specialties for a specific property vendor type
 * @param vendorTypeCode - The vendor type code to get specialties for
 * @returns Promise<DropdownOption[]> - Array of specialty options for that vendor type
 */
export async function getPropertyVendorSpecialtiesByType(vendorTypeCode: string): Promise<DropdownOption[]> {
  if (!PROPERTY_VENDOR_SPECIALTY_MAP_API || Object.keys(PROPERTY_VENDOR_SPECIALTY_MAP_API).length === 0) {
    PROPERTY_VENDOR_SPECIALTY_MAP_API = await buildPropertyVendorSpecialtyMap();
  }

  return PROPERTY_VENDOR_SPECIALTY_MAP_API[vendorTypeCode] || [{ id: '', value: '' }];
}

/**
 * Build grouped service code options for property vendors
 * Groups specialty codes by their vendor type for organized selection
 * @returns Promise<Array<{id: string, value: string, group: string}>> - Grouped service code options
 */
export async function buildPropertyServiceCodeOptions(): Promise<Array<{ id: string, value: string, group: string }>> {
  // Check if we already have cached data
  if (PROPERTY_SERVICE_CODE_OPTIONS_GROUPED.length > 0) {
    return PROPERTY_SERVICE_CODE_OPTIONS_GROUPED;
  }

  // Fetch vendor types and specialties
  const [vendorTypes, specialtyMap] = await Promise.all([
    getPropertyVendorTypeOptions(),
    buildPropertyVendorSpecialtyMap()
  ]);

  const groupedOptions: Array<{ id: string, value: string, group: string }> = [];

  // Iterate through vendor types and build grouped options
  vendorTypes.forEach(vendorType => {
    if (!vendorType.id) return; // Skip empty option

    const specialties = specialtyMap[vendorType.id] || [];
    const groupName = vendorType.value;

    specialties.forEach(specialty => {
      if (!specialty.id) return; // Skip empty option

      groupedOptions.push({
        id: specialty.id,
        value: specialty.value,
        group: groupName
      });
    });
  });

  // Cache the result
  PROPERTY_SERVICE_CODE_OPTIONS_GROUPED = groupedOptions;

  return groupedOptions;
}




// Initialize with empty arrays - will be populated dynamically
let CUSTOMER_NETWORK_CODES: DropdownOption[] = [];
let APPRAISAL_SOFTWARE_OPTIONS_API: DropdownOption[] = [];
let PROVIDER_STATUS_OPTIONS_API: DropdownOption[] = [];
let VENDOR_TYPE_CODE_OPTIONS_API: DropdownOption[] = [];
//let PROPERTY_VENDOR_TYPE_OPTIONS_API: DropdownOption[] = [];
let PROPERTY_VENDOR_SPECIALTY_MAP_API: Record<string, DropdownOption[]> = {};
let PROPERTY_SERVICE_CODE_OPTIONS_GROUPED: Array<{ id: string, value: string, group: string }> = [];

export interface GroupedFieldConfig {
  title?: string;
  fields: FieldConfig[];
  rows: string[][];
}

// Define property-specific options

const YES_NO_OPTIONS = [
  { id: 'Yes', value: 'Yes' },
  { id: 'No', value: 'No' }
];

const SERVICE_AREA_OPTIONS = [
  { id: '', value: '' },
  { id: 'Nationwide', value: 'Nationwide' },
  { id: 'Statewide', value: 'Statewide' },
  { id: 'Countywide', value: 'Countywide' },
  { id: 'Zipwide', value: 'Zipwide' }
];



export interface UpdateProviderFormConfig {
  title?: string;
  groups: GroupedFieldConfig[];
  initialValues: InitialValues;
}

// Edit Provider Configuration with grouped fields
export const AUTO_EDIT_PROVIDER_CONFIG: UpdateProviderFormConfig = {
  // title: "Provider",
  groups: [
    {
      title: "Provider Information",
      fields: [
        {
          name: 'providerName',
          type: 'text',
          label: 'Provider Name',
          colSize: '8',
          colSizeSm: '8',
          colSizeMd: '8',
          colSizeLg: '8',
          validation: {
            required: true
          }
        },
        {
          name: 'taxIdTin',
          type: 'number',
          label: 'Tax ID / TIN',
          colSize: '8',
          colSizeSm: '4',
          colSizeMd: '4',
          colSizeLg: '4',
          limitations: {
            pattern: ZIP_INPUT_REGEX,
          }
        },
        {
          name: 'tiesId',
          type: 'number',
          label: 'Ties ID',
          colSize: '8',
          colSizeSm: '4',
          colSizeMd: '4',
          colSizeLg: '4',
        },
        {
          name: 'appraisalSoftware1',
          type: 'select',
          label: 'Appraisal Software 1',
          colSize: '8',
          colSizeSm: '4',
          colSizeMd: '4',
          colSizeLg: '4',
          options: [] // Will be populated by loadProviderConfig()
        },
        {
          name: 'appraisalSoftware2',
          type: 'select',
          label: 'Appraisal Software 2',
          colSize: '8',
          colSizeSm: '4',
          colSizeMd: '4',
          colSizeLg: '4',
          options: [] // Will be populated by loadProviderConfig()
        },
        {
          name: 'suppressionIndicator',
          type: 'select',
          label: 'Provider Status',
          colSize: '8',
          colSizeSm: '8',
          colSizeMd: '8',
          colSizeLg: '8',
          options: [], // Will be populated by loadProviderConfig()
          validation: {
            required: true
          }
        },
        {
          name: 'suppressionComment',
          type: 'text',
          label: 'Suppression Comment',
          colSize: '8',
          colSizeSm: '8',
          colSizeMd: '8',
          colSizeLg: '8',
        }
      ],
      rows: [
        ['providerName'],
        ['taxIdTin', 'tiesId'],
        ['appraisalSoftware1', 'appraisalSoftware2'],
        ['suppressionIndicator'],
        ['suppressionComment']
      ]
    },
    {
      title: "Vendor Information",
      fields: [
        {
          name: 'vendorId',
          type: 'text',
          label: 'Vendor ID',
          colSize: '8',
          colSizeSm: '4',
          colSizeMd: '4',
          colSizeLg: '4',
          validation: {
            required: true
          },
          limitations: {
            pattern: ALPHANUMERIC_REGEX
          }
        },
        {
          name: 'vendorTypeCode',
          type: 'select',
          label: 'Vendor Type',
          colSize: '8',
          colSizeSm: '4',
          colSizeMd: '4',
          colSizeLg: '4',
          disabled: true,
          options: [], // Will be populated by loadProviderConfig()
        },
        {
          name: 'vendorSpecializations',
          type: 'text',
          label: 'Vendor Specializations',
          colSize: '8',
          colSizeSm: '8',
          colSizeMd: '8',
          colSizeLg: '8',
          validation: {
            // maxLength: 10
          }
        }
      ],
      rows: [
        ['vendorId', 'vendorTypeCode'],
        ['vendorSpecializations']
      ]
    },
    {
      title: "Contact and Location Information",
      fields: [
        {
          name: 'phone',
          type: 'text',
          label: 'Business Phone Number',
          colSize: '8',
          colSizeSm: '8',
          colSizeMd: '8',
          colSizeLg: '8',
          validation: {
            // required: true,
            pattern: NUMERIC_ONLY_REGEX,
            minLength: 10,
            // maxLength: 10
            required: true
          },
          limitations: {
            pattern: NUMERIC_ONLY_REGEX,
            // maxLength: 10
          }
        },
        {
          name: 'address',
          type: 'text',
          label: 'Address',
          colSize: '8',
          colSizeSm: '8',
          colSizeMd: '8',
          colSizeLg: '8',
          validation: {
            required: true
          }
        },
        {
          name: 'city',
          type: 'text',
          label: 'City',
          colSize: '8',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',
          validation: {
            required: true
          }
        },
        {
          name: 'state',
          type: 'select',
          label: 'State',
          colSize: '8',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',
          options: [],
          validation: {
            required: true
          }
        },
        {
          name: 'zip',
          type: 'number',
          label: 'ZIP',
          colSize: '8',
          colSizeSm: '2',
          colSizeMd: '2',
          colSizeLg: '2',
          validation: {
            required: true
          },
          limitations: {
            maxLength: 5,
            pattern: ZIP_INPUT_REGEX,
          }
        }
      ],
      rows: [
        ['phone'],
        ['address'],
        ['city', 'state', 'zip'],
      ]
    }
  ],
  initialValues: {
    // Provider Information
    providerName: '',
    providerId: '',
    specialityCodeSVC1: '',
    taxIdTin: '',
    tiesId: '',
    appraisalSoftware1: '',
    appraisalSoftware2: '',
    suppressionIndicator: '',

    // Vendor Information
    vendorId: '',
    vendorTypeCode: '11',
    vendorSpecializations: '',

    // Contact Information
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  }
};

export const CONTRACT_EDIT_PROVIDER_CONFIG: UpdateProviderFormConfig = {
  groups: [
    {
      title: "Provider Information",
      fields: [
        {
          name: 'vendorName',
          type: 'text',
          label: 'Provider Name',
          colSize: '8',
          colSizeSm: '8',
          colSizeMd: '8',
          colSizeLg: '8',
          validation: {
            required: true
          }
        },
        {
          name: 'city',
          type: 'text',
          label: 'Provider City',
          colSize: '8',
          colSizeSm: '4',
          colSizeMd: '4',
          colSizeLg: '4',
          validation: {
            // required: true
          }
        },
        {
          name: 'state',
          type: 'select',
          label: 'Provider State',
          colSize: '8',
          colSizeSm: '4',
          colSizeMd: '4',
          colSizeLg: '4',
          options: [],
          validation: {
            required: true
          }
        },
        {
          name: 'vendorSpeciality',
          type: 'multiselecttag',
          label: 'Vendor Speciality (SVC1)',
          colSize: '8',
          colSizeSm: '8',
          colSizeMd: '8',
          colSizeLg: '8',
          options: [], //filled by api call
          filter: true,
          validation: {
            required: true
          }
        },
        {
          name: 'rateLink',
          type: 'textarea',
          label: 'Rate Link',
          colSize: '8',
          colSizeSm: '8',
          colSizeMd: '8',
          colSizeLg: '8',
        }
      ],
      rows: [
        ['vendorName'],
        ['city', 'state'],
        ['vendorSpeciality'],
        ['rateLink']
      ]
    },
    {
      title: "Contract Owner",
      fields: [
        {
          name: 'contractOwnerName',
          type: 'text',
          label: 'Travelers Contract Owner Name',
          colSize: '8',
          colSizeSm: '8',
          colSizeMd: '8',
          colSizeLg: '8',
          validation: {
            required: true
          }
        },
        {
          name: 'contractType',
          type: 'select',
          label: 'Contract Type',
          colSize: '8',
          colSizeSm: '4',
          colSizeMd: '4',
          colSizeLg: '4',
          options: [], // Will be populated by loadProviderConfig()
          validation: {
            required: true
          }
        },
        {
          name: 'contractOwnerDivision',
          type: 'text',
          label: 'Contract Owner Division',
          colSize: '8',
          colSizeSm: '4',
          colSizeMd: '4',
          colSizeLg: '4',
          validation: {
            required: true
          }
        }
      ],
      rows: [
        ['contractOwnerName'],
        ['contractType', 'contractOwnerDivision']
      ]
    },
    {
      title: "Additional Details",
      fields: [
        {
          name: 'status',
          type: 'select',
          label: 'Provider Status',
          colSize: '8',
          colSizeSm: '8',
          colSizeMd: '8',
          colSizeLg: '8',
          options: [], // Will be populated by loadProviderConfig()
          validation: {
            required: true
          }
        },
        {
          name: 'vendorTypeCode',
          type: 'select',
          label: 'Vendor Type',
          colSize: '8',
          colSizeSm: '8',
          colSizeMd: '8',
          colSizeLg: '8',
          options: [], // Will be populated by loadProviderConfig()
          disabled: true
        }
      ],
      rows: [
        ['status'],
        ['vendorTypeCode']
      ]
    }
  ],
  initialValues: {
    // Provider Information
    vendorName: '',
    city: '',
    state: '',
    vendorSpeciality: '',
    rateLink: '',

    // Contract Information
    contractOwnerName: '',
    contractType: '',
    contractOwnerDivision: '',
    // Additional Information
    status: '',
    vendorTypeCode: '70'
  }
};

// Generic export for backward compatibility
export const EDIT_PROVIDER_CONFIG = AUTO_EDIT_PROVIDER_CONFIG;

// Future provider type configurations can be added here
export const PROPERTY_EDIT_PROVIDER_CONFIG: UpdateProviderFormConfig = {
  title: "Edit Property Provider",
  groups: [
    {
      title: "Basic Provider Information",
      fields: [
        {
          name: 'providerName',
          type: 'text',
          label: 'Provider Name',
          colSize: '8',
          validation: {
            required: true
          }
        },
        {
          name: 'dba',
          type: 'text',
          label: 'Doing Business As (DBA)',
          colSize: '8'
        },
        {
          name: 'taxIdTin',
          type: 'text',
          label: 'Tax ID/TIN',
          colSize: '8'
        },
        {
          name: 'status',
          type: 'select',
          label: 'Provider Status',
          colSize: '8',
          options: [], // Will be populated by loadProviderConfig()
          validation: {
            required: true
          }
        },

        {
          name: 'ExternalProviderID',
          label: 'External Provider ID',
          type: 'text',
          colSize: '8'
        },

        {
          name: 'vendorId',
          type: 'text',
          label: 'Vendor ID',
          colSize: '5'
        },
        {
          name: 'vendorTypeCode',
          type: 'select',
          label: 'Vendor Type',
          colSize: '5',
          options: [], // Will be populated by loadProviderConfig()
          disabled: true
        },
      ],
      rows: [
        ['providerName'],
        ['dba'],
        ['taxIdTin'],
        ['status'],
        ['ExternalProviderID'],
        ['vendorId', 'vendorTypeCode']
      ]
    },
    {
      title: "Business Details",
      fields: [
        {
          name: 'businessStartDate',
          type: 'datePicker',
          label: 'Business Start Date',
          colSize: '5'
        },
        {
          name: 'phone',
          type: 'text',
          label: 'Business Phone Number',
          colSize: '5',
          validation: {
            pattern: NUMERIC_ONLY_REGEX,
            minLength: 10,
            required: true
          }
        },
        {
          name: 'principalOwner',
          type: 'text',
          label: 'Principal Owner Name',
          colSize: '8'
        },
        {
          name: 'franchiseId',
          type: 'text',
          label: 'Franchise ID',
          colSize: '8'
        },
      ],
      rows: [
        ['businessStartDate', 'phone'],
        ['principalOwner'],
        ['franchiseId']
      ]
    },
    {
      title: "Primary Address",
      fields: [
        {
          name: 'address',
          type: 'text',
          label: 'Address',
          colSize: '8',
          validation: {
            required: true
          }
        },
        {
          name: 'city',
          type: 'text',
          label: 'City',
          colSize: '4',
          validation: {
            required: true
          }
        },
        {
          name: 'state',
          type: 'select',
          label: 'State',
          colSize: '3',
          options: [],
          validation: {
            required: true
          }
        },
        {
          name: 'zip',
          type: 'text',
          label: 'ZIP',
          colSize: '3',
          validation: {
            required: true
          },
          limitations: {
            maxLength: 5,
            pattern: ZIP_INPUT_REGEX,
          }
        },

      ],
      rows: [
        ['address'],
        ['city', 'state', 'zip'],
      ]
    },
    {
      title: "Mailing Address",
      fields: [
        {
          name: 'mailingStreet',
          type: 'text',
          label: 'Mailing Address',
          colSize: '9'
        },
        {
          name: 'mailingCity',
          type: 'text',
          label: 'Mailing City',
          colSize: '5'
        },
        {
          name: 'mailingState',
          type: 'select',
          label: 'Mailing State',
          colSize: '3',
          options: [],
        },
        {
          name: 'mailingZip',
          type: 'text',
          label: 'Mailing Zip',
          colSize: '3',
          limitations: {
            maxLength: 5,
            pattern: ZIP_INPUT_REGEX,
          }
        }
      ],
      rows: [
        ['mailingStreet'],
        ['mailingCity', 'mailingState', 'mailingZip']
      ]
    },
    {
      title: "Contact Information",
      fields: [
        {
          name: 'providerContactName',
          type: 'text',
          label: 'Provider Contact Name',
          colSize: '5',
          validation: {
            required: true
          }
        },
        {
          name: 'providerContactEmail',
          type: 'email',
          label: 'Provider Contact Email',
          colSize: '5',
          validation: {
            required: true
          }
        },
        {
          name: 'additionalContactName',
          type: 'text',
          label: 'Additional Provider Contact Name',
          colSize: '8'
        },
        {
          name: 'afterHoursPhone',
          type: 'text',
          label: 'After Hours Phone',
          colSize: '8'
        }
      ],
      rows: [
        ['providerContactName', 'providerContactEmail'],
        ['additionalContactName'],
        ['afterHoursPhone']
      ]
    },
    {
      title: "Credentialing Contacts",
      fields: [
        {
          name: 'credentialProviderContactName',
          type: 'text',
          label: 'Provider Contact Name (Credentialing)',
          colSize: '4'
        },
        {
          name: 'credentialProviderContactEmail',
          type: 'email',
          label: 'Provider Contact Email (Credentialing)',
          colSize: '4'
        },
      ],
      rows: [
        ['credentialProviderContactName', 'credentialProviderContactEmail'],
      ]
    },
    {
      title: "Licensing and Compliance",
      fields: [
        {
          name: 'localLicense',
          type: 'text',
          label: 'Local License Number ',
          colSize: '8'
        },
        {
          name: 'localLicenseComment',
          type: 'text',
          label: 'Local License Comment',
          colSize: '8'
        },
        {
          name: 'stateLicense',
          type: 'text',
          label: 'State License Number',
          colSize: '8'
        },
        {
          name: 'stateLicenseComment',
          type: 'text',
          label: 'State License Comment',
          colSize: '8'
        },
      ],
      rows: [
        ['localLicense'],
        ['localLicenseComment'],
        ['stateLicense'],
        ['stateLicenseComment']
      ]
    },
    {
      title: "Service Area",
      fields: [

        {
          name: 'serviceArea',
          type: 'select',
          label: 'Service Area Type',
          colSize: '8',
          options: SERVICE_AREA_OPTIONS
        },
        {
          name: 'stateServiceAreas',
          type: 'multiselecttag', // custom type for your renderer
          label: 'State Service Areas',
          options: [],
          colSize: '8',
          showIf: { field: 'serviceArea', value: 'Statewide' } // only show if Statewide is selected
        },
        {
          name: 'countyState',
          type: 'select',
          label: 'State Service Area',
          options: [],
          colSize: '4',
          validation: {
            required: true,
          },
          showIf: { field: 'serviceArea', value: 'Countywide' }
        },
        {
          name: 'countyServiceArea',
          type: 'text',
          label: 'County Service Area',
          colSize: '4',
          showIf: { field: 'serviceArea', value: 'Countywide' },
          validation: {
            required: true,
            pattern: {
              value: /^[a-zA-Z\s]+(,\s*[a-zA-Z\s]+)*$/,
              message: 'Enter one or more county names separated by comma and space (e.g., "County1, County2")'
            }
          },
          limitations: {
            pattern: COUNTY_INPUT_REGEX
          },
          helpText: 'Enter county names separated by comma and space'
        },
        {
          name: 'zipServiceAreas',
          type: 'textarea',
          label: 'Zip Service Area',
          colSize: '8',
          showIf: { field: 'serviceArea', value: 'Zipwide' },
          validation: {
            required: true,
            pattern: {
              value: /^(\d{5}|\d{5}-\d{4})(, (\d{5}|\d{5}-\d{4}))*$/,
              message: 'Format must be: 12345 or 12345-6789, separated by comma and space for multiple entries'
            }
          },
          limitations: {
            pattern: ZIP_INPUT_REGEX
          },
          helpText: 'Enter ZIP codes (12345 or 12345-6789) separated by comma and space'
        }
      ],
      rows: [
        ['serviceArea'],
        ['stateServiceAreas'],
        ['countyState', 'countyServiceArea'],
        ['zipServiceAreas'],
      ]
    },
    {
      title: "Services and Specializations",
      fields: [
        {
          name: 'serviceCodes',
          type: 'multiselecttag',
          label: 'Service Code(s)',
          colSize: '8',
          options: [], // Will be populated by loadProviderConfig()
          maxSelection: 8,
          helpText: 'Select up to 8',
          filter: true,
          groupBy: 'group'
        },
        {
          name: 'otherServices',
          type: 'text',
          label: 'Other Services',
          colSize: '8',
          placeholder: 'Enter additional services not listed above'
        }
      ],
      rows: [
        ['serviceCodes'],
        ['otherServices']
      ]
    },
    {
      title: "Service Capabilities",
      fields: [
        {
          name: 'subcontractorUsage',
          type: 'text',
          label: 'Percentage of Work Done by Subcontractors',
          colSize: '8'
        },
        {
          name: 'twentyFourHourResponse',
          type: 'radio',
          label: '24-Hour Response',
          colSize: '8',
          options: YES_NO_OPTIONS,
          validation: {
            required: true
          }
        },
      ],
      rows: [
        ['subcontractorUsage'],
        ['twentyFourHourResponse'],
      ]
    },
    {
      title: "System Information",
      fields: [
        {
          name: 'customerNetworkCodes',
          type: 'select',
          label: 'Customer Network Codes',
          colSize: '8',
          options: [] // Will be populated by loadProviderConfig()
        },
        {
          name: 'claimCenter',
          type: 'select',
          label: 'Claim Center',
          colSize: '8',
          options: [] // Will be populated by loadProviderConfig()
        }
      ],
      rows: [
        ['customerNetworkCodes'],
        ['claimCenter']
      ]
    },
    {
      title: "Additional Information",
      fields: [
        {
          name: 'preferredProviderStatus',
          type: 'radio',
          label: 'Preferred Provider Status',
          colSize: '8',
          options: YES_NO_OPTIONS,
          validation: {
            required: true
          }
        },
        {
          name: 'warrantyPeriod',
          type: 'number',
          label: 'Warranty (in Months)',
          colSize: '8'
        },
        {
          name: 'adminComments',
          type: 'textarea',
          label: 'Comments',
          colSize: '8'
        },
        {
          name: 'notes',
          type: 'textarea',
          label: 'Administrative Notes',
          colSize: '8'
        },
      ],
      rows: [
        ['preferredProviderStatus'],
        ['warrantyPeriod'],
        ['adminComments'],
        ['notes']
      ]
    }
  ],
  initialValues: {
    // Provider Identity
    providerName: '',
    dba: '',
    taxIdTin: '',
    vendorId: '',
    status: 'Active',
    vendorTypeCode: '60',

    // Contact Information
    primaryContact: '',
    primaryContactEmail: '',
    additionalContactName: '',
    credentialProviderContactName: '',
    credentialProviderContactEmail: '',
    phone: '',
    afterHoursPhone: '',

    // Primary Location
    address: '',
    city: '',
    state: '',
    zip: '',
    // primaryCounty: '',
    lat: '',
    lon: '',

    // Mailing Address
    mailingStreet: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: '',

    // Business Details
    principalOwner: '',
    businessStartDate: '',
    // yearsInBusiness: '',
    // Business Details (continued)
    // yearEstablished: '',
    franchiseId: '',
    localLicense: '',
    localLicenseComment: '',
    stateLicense: '',
    stateLicenseComment: '',
    subcontractorUsage: '',

    // Service Capabilities
    nationwide: 'false',
    serviceAreasStates: '',
    serviceAreasCounties: '',
    serviceAreasZipcodes: '',
    // serviceAreaType: '',
    areasWorkedIn: '',

    // Service Codes
    serviceCodes: '',
    otherServices: '',

    // Additional Service Information
    warrantyPeriod: '',
    twentyFourHourResponse: 'false',
    preferredProviderStatus: 'false',

    // System Information
    customerNetworkCodes: '',
    claimCenter: '',
    adminComments: '',
    notes: '',

    // Selection Status
    isSelected: 'false'
  }
};

/**
 * Load provider configuration with dynamic reference data from API
 * This function fetches the latest reference codes and populates the dropdowns
 * @param configType - Type of configuration to load ('auto', 'property', 'contract')
 * @returns Promise<UpdateProviderFormConfig> - Configuration with populated options
 */
export async function loadProviderConfig(configType: 'auto' | 'property' | 'contract' = 'property'): Promise<UpdateProviderFormConfig> {
  // Clone the config based on type
  let config: UpdateProviderFormConfig;

  if (configType === 'property') {
    config = structuredClone(PROPERTY_EDIT_PROVIDER_CONFIG);
  } else if (configType === 'auto') {
    config = structuredClone(AUTO_EDIT_PROVIDER_CONFIG);
  } else if (configType === 'contract') {
    config = structuredClone(CONTRACT_EDIT_PROVIDER_CONFIG);
  } else {
    // Default to property config
    config = structuredClone(PROPERTY_EDIT_PROVIDER_CONFIG);
  }

  // Load reference data from API
  const [customerNetworkCodes, claimCenterOptions, appraisalSoftwareOptions, providerStatusOptions, vendorTypeCodeOptions, stateOptions, propertyServiceCodeOptions] = await Promise.all([
    getCustomerNetworkCodes(),
    getClaimCenterOptions(),
    getAppraisalSoftwareOptions(),
    getProviderStatusOptions(),
    getVendorTypeCodeOptions(),
    getStateOptions(),
    configType === 'property' ? buildPropertyServiceCodeOptions() : Promise.resolve([])
  ]);

  // Store in module-level variables for backward compatibility
  CUSTOMER_NETWORK_CODES = customerNetworkCodes;
  //CLAIM_CENTER_OPTIONS = claimCenterOptions;
  APPRAISAL_SOFTWARE_OPTIONS_API = appraisalSoftwareOptions;
  PROVIDER_STATUS_OPTIONS_API = providerStatusOptions;
  VENDOR_TYPE_CODE_OPTIONS_API = vendorTypeCodeOptions;

  // Find and update the Provider Information group with loaded options
  const providerInfoGroup = config.groups.find(group => group.title === 'Provider Information');

  if (providerInfoGroup) {
    // Update appraisalSoftware1 field (auto config only)
    const appraisalSoftware1Field = providerInfoGroup.fields.find(f => f.name === 'appraisalSoftware1');
    if (appraisalSoftware1Field) {
      appraisalSoftware1Field.options = appraisalSoftwareOptions;
    }

    // Update appraisalSoftware2 field (auto config only)
    const appraisalSoftware2Field = providerInfoGroup.fields.find(f => f.name === 'appraisalSoftware2');
    if (appraisalSoftware2Field) {
      appraisalSoftware2Field.options = appraisalSoftwareOptions;
    }

    // Update suppressionIndicator field (Provider Status dropdown) - auto config only
    const suppressionIndicatorField = providerInfoGroup.fields.find(f => f.name === 'suppressionIndicator');
    if (suppressionIndicatorField) {
      suppressionIndicatorField.options = providerStatusOptions;
    }
  }

  // Find and update the Vendor Information group with loaded options (AUTO config)
  const vendorInfoGroup = config.groups.find(group => group.title === 'Vendor Information');

  if (vendorInfoGroup) {
    // Update vendorTypeCode field
    const vendorTypeCodeField = vendorInfoGroup.fields.find(f => f.name === 'vendorTypeCode');
    if (vendorTypeCodeField) {
      vendorTypeCodeField.options = vendorTypeCodeOptions;
    }
  }

  // Ensure all state-like fields across all groups use API-driven options
  const stateFieldNames = new Set(['state', 'mailingState', 'stateServiceAreas', 'countyState']);
  config.groups.forEach(group => {
    group.fields.forEach(field => {
      if (stateFieldNames.has(field.name)) {
        (field as any).options = stateOptions;
      }
    });
  });

  // Find and update the Basic Provider Information group (property config)
  const basicProviderInfoGroup = config.groups.find(group => group.title === 'Basic Provider Information');

  if (basicProviderInfoGroup) {
    // Update status field
    const statusField = basicProviderInfoGroup.fields.find(f => f.name === 'status');
    if (statusField) {
      statusField.options = providerStatusOptions;
    }

    // Update vendorTypeCode field
    const vendorTypeCodeField = basicProviderInfoGroup.fields.find(f => f.name === 'vendorTypeCode');
    if (vendorTypeCodeField) {
      vendorTypeCodeField.options = vendorTypeCodeOptions;
    }


    // Update state field
    const basicStateField = basicProviderInfoGroup.fields.find(f => f.name === 'state');
    if (basicStateField) {
      basicStateField.options = stateOptions;
    }
  }

  // Find and update the Additional Details group (contract config)
  const additionalDetailsGroup = config.groups.find(group => group.title === 'Additional Details');

  if (additionalDetailsGroup) {
    // Update status field
    const statusField = additionalDetailsGroup.fields.find(f => f.name === 'status');
    if (statusField) {
      statusField.options = providerStatusOptions;
    }

    // Update vendorTypeCode field (contract config uses vendorTypeCode)
    const vendorTypeCodeFieldContract = additionalDetailsGroup.fields.find(f => f.name === 'vendorTypeCode');
    if (vendorTypeCodeFieldContract) {
      vendorTypeCodeFieldContract.options = vendorTypeCodeOptions;
    }

  }

  // Find and update the Contact and Location Information group - both AUTO and PROPERTY configs
  const contactLocationGroup = config.groups.find(group => group.title === 'Contact and Location Information');

  if (contactLocationGroup) {
    // Update state field
    const stateField = contactLocationGroup.fields.find(f => f.name === 'state');
    if (stateField) {
      stateField.options = stateOptions;
    }
  }

  // Find and update the System Information group with loaded options
  const systemInfoGroup = config.groups.find(group => group.title === 'System Information');

  if (systemInfoGroup) {
    // Update customerNetworkCodes field
    const customerNetworkField = systemInfoGroup.fields.find(f => f.name === 'customerNetworkCodes');
    if (customerNetworkField) {
      customerNetworkField.options = customerNetworkCodes;
    }

    // Update claimCenter field
    const claimCenterField = systemInfoGroup.fields.find(f => f.name === 'claimCenter');
    if (claimCenterField) {
      claimCenterField.options = claimCenterOptions;
    }
  }

  // Find and update the Services and Specializations group (property config)
  const servicesGroup = config.groups.find(group => group.title === 'Services and Specializations');

  if (servicesGroup) {
    const serviceCodesField = servicesGroup.fields.find(f => f.name === 'serviceCodes');
    if (serviceCodesField) {
      serviceCodesField.options = propertyServiceCodeOptions;
    }
  }

  return config;
}

/**
 * Synchronous version that returns config with empty options
 * Use this only if you need immediate access to config structure
 * For actual forms, prefer loadProviderConfig() which loads API data
 */
export function getProviderConfigSync(configType: 'auto' | 'property' | 'contract' = 'property'): UpdateProviderFormConfig {
  if (configType === 'property') {
    return PROPERTY_EDIT_PROVIDER_CONFIG;
  } else if (configType === 'auto') {
    return AUTO_EDIT_PROVIDER_CONFIG;
  } else {
    return PROPERTY_EDIT_PROVIDER_CONFIG;
  }
}


